import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Building2,
  Layers,
  Calendar,
  Plus,
  Target,
  X,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import {
  useRoom,
  useBuilding,
  useRoomAssets,
  useAssets,
  useOrganization,
  useUpdateRoom,
} from '@/hooks'
import { api } from '@/services'
import {
  PageHeader,
  NotFound,
  HistoryTimeline,
  RoomQRCode,
  ComplianceTargetForm,
} from '@/components'
import type { InheritedTargetHint } from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { queryClient } from '@/lib/query'
import type { Asset, ComplianceTarget } from '@/types'
import { RoomFormDialog } from '../buildings/RoomFormDialog'

export function RoomDetailPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()

  const { data: room, isLoading, error } = useRoom(roomId ?? '')
  const { data: building } = useBuilding(room?.building_id ?? '')
  const { data: roomAssets, isLoading: assetsLoading } = useRoomAssets(roomId ?? '')
  const { data: allAssets } = useAssets(effectiveOrgId)
  const { data: org } = useOrganization(effectiveOrgId)
  const updateRoomMutation = useUpdateRoom(roomId ?? '', room?.building_id ?? '')

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addingAsset, setAddingAsset] = useState(false)
  const [selectedAssetId, setSelectedAssetId] = useState('')

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  // Get list of asset IDs already assigned to this room
  const assignedAssetIds = new Set(roomAssets?.map((a) => a.id) ?? [])

  // Get assets not yet assigned
  const availableAssets = allAssets?.filter((asset) => !assignedAssetIds.has(asset.id))

  async function addAssetToRoom(assetId: string) {
    try {
      await api.post(`/rooms/${roomId}/assets`, { asset_id: assetId })
      // Invalidate room assets query
      queryClient.invalidateQueries({ queryKey: ['assets', { roomId }] })
      setSelectedAssetId('')
      setAddingAsset(false)
    } catch (err) {
      console.error('Failed to add asset to room:', err)
    }
  }

  async function removeAssetFromRoom(assetId: string) {
    try {
      await api.delete(`/rooms/${roomId}/assets/${assetId}`)
      // Invalidate room assets query
      queryClient.invalidateQueries({ queryKey: ['assets', { roomId }] })
    } catch (err) {
      console.error('Failed to remove asset from room:', err)
    }
  }

  async function handleSaveTarget(target: ComplianceTarget | null) {
    try {
      await updateRoomMutation.mutateAsync({ target })
      toast.success(
        target === null ? 'Room compliance target cleared' : 'Room compliance target saved'
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save target'
      toast.error(msg)
      throw err
    }
  }

  // Resolve the nearest inherited default to surface as the "Inheriting
  // from …" hint when the room has no target of its own. Building default
  // beats org default; if neither is set, the form falls back to "No
  // target set". The resolution mirrors the backend EffectiveTarget
  // helper but operates on the already-loaded Building / Organization
  // records — no extra round-trips.
  function resolveInherited(): InheritedTargetHint | null {
    if (building?.default_target) {
      return {
        label: `Building default (${building.name})`,
        target: building.default_target,
      }
    }
    if (org?.default_target) {
      return { label: 'Organization default', target: org.default_target }
    }
    return null
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !room) {
    return <NotFound onBack={() => navigate('/rooms')} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(room.building_id ? `/buildings/${room.building_id}` : '/rooms')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          title={room.name}
          description="Room details and assigned assets"
          actions={
            canEdit && (
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )
          }
        />
      </div>

      {/* Room Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Room Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Building</p>
                <Link
                  to={`/buildings/${room.building_id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {building?.name ?? 'Loading...'}
                </Link>
              </div>
            </div>
            {room.floor && (
              <div className="flex items-start gap-3">
                <Layers className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Floor</p>
                  <p className="font-medium">{room.floor}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Created</p>
                <p className="font-medium">{new Date(room.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Target Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5" />
            Compliance target
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ComplianceTargetForm
            value={room.target ?? null}
            inheritedFrom={resolveInherited()}
            canEdit={canEdit}
            onSave={handleSaveTarget}
            toggleLabel="Override inherited target"
          />
        </CardContent>
      </Card>

      {/* Assets Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Assigned Assets ({roomAssets?.length ?? 0})</CardTitle>
          {canEdit && !addingAsset && (availableAssets?.length ?? 0) > 0 && (
            <Button variant="outline" size="sm" onClick={() => setAddingAsset(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Asset
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {addingAsset && (
            <div className="flex items-center gap-2 mb-4 p-3 border rounded-lg bg-muted/50">
              <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select an asset to add" />
                </SelectTrigger>
                <SelectContent>
                  {availableAssets?.map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!selectedAssetId}
                onClick={() => addAssetToRoom(selectedAssetId)}
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setAddingAsset(false)
                  setSelectedAssetId('')
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {assetsLoading ? (
            <div className="text-muted-foreground text-center py-4">Loading assets...</div>
          ) : roomAssets && roomAssets.length > 0 ? (
            <div className="space-y-2">
              {roomAssets.map((asset: Asset) => (
                <div
                  key={asset.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{asset.name}</p>
                    {asset.description && (
                      <p className="text-sm text-muted-foreground">{asset.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">Asset</Badge>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAssetFromRoom(asset.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No assets assigned to this room yet.</p>
              {canEdit && (availableAssets?.length ?? 0) > 0 && (
                <Button variant="outline" className="mt-4" onClick={() => setAddingAsset(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Asset
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Code */}
      {canEdit && (
        <RoomQRCode roomId={roomId ?? ''} roomName={room.name} buildingName={building?.name} />
      )}

      {/* History */}
      <HistoryTimeline
        entityType="rooms"
        entityId={roomId ?? ''}
        createdAt={room.created_at}
        updatedAt={room.updated_at}
        updatedBy={room.updated_by}
        updatedByName={room.updated_by_name}
      />

      {editDialogOpen && (
        <RoomFormDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          buildingId={room.building_id}
          room={room}
        />
      )}
    </div>
  )
}
